import { useCallback } from 'react';
import {
  CapaErrorCode,
  CreatePurchaseOrderInput,
  FetchPurchaseOrdersDocument,
  useCreatePurchaseOrderMutation
} from '@generated/graphql';
import { storage } from '@utils/storage';
import useBaseData from '../useBaseData';

interface UseCreatePurchaseOrderArgs {
  rfqId: string;
  partnerId: string;
  chatRoomId: string;
}

const useCreatePurchaseOrder = ({
  rfqId,
  partnerId,
  chatRoomId
}: UseCreatePurchaseOrderArgs) => {
  const { data: baseData } = useBaseData({
    rfqId,
    partnerId
  });

  const [createPurchaseOrder] = useCreatePurchaseOrderMutation();

  /** 모바일 결제시 리다이렉트 후 데이터 유지를 위해 local에 저장 */
  const setTempDatas = useCallback(
    (purchaseOrderId: string) => {
      storage.setItem.TEMP_SELECTED_QUOTATION_INFO({
        rfqId,
        partnerId,
        quotationId: baseData?.quotationId ?? '',
        chatRoomId
      });

      storage.setItem.TEMP_CREATE_PURCAHSE_ORDER_ID(purchaseOrderId);
    },
    [partnerId, baseData?.quotationId, rfqId, chatRoomId]
  );

  const onCreatePurchaseOrder = useCallback(
    async (input: CreatePurchaseOrderInput) => {
      const response = await createPurchaseOrder({
        variables: { input },
        refetchQueries: [
          {
            query: FetchPurchaseOrdersDocument,
            variables: {
              input: {
                rfqId,
                quotationPartnerId: partnerId
              }
            }
          }
        ],
        awaitRefetchQueries: true
      });

      const responseData = response.data?.createPurchaseOrder;
      const { result, data } = responseData ?? {};
      const isCreateOrderSuccess = result === CapaErrorCode.Success;
      const purchaseOrderId = data?.purchaseOrderId;

      if (!isCreateOrderSuccess || !purchaseOrderId) {
        switch (result) {
          case CapaErrorCode.RfqPaymentInProgress:
            throw new Error(
              '이미 발행된 발주서가 있습니다. 발주서를 확인해주세요.'
            );
          case CapaErrorCode.NotLatestQuotation:
            throw new Error(
              '가장 최근에 발행된 견적서로만 발주서 발행이 가능합니다. 견적서를 확인해주세요.'
            );
          case CapaErrorCode.RfqExpired:
            throw new Error('마감된 견적요청서입니다.');
          default:
            throw new Error('발주서 발행에 실패했습니다. 다시 시도해주세요.');
        }
      } else {
        setTempDatas(purchaseOrderId);
      }

      return purchaseOrderId;
    },
    [rfqId, createPurchaseOrder, setTempDatas, partnerId]
  );

  return { onCreatePurchaseOrder };
};

export default useCreatePurchaseOrder;
